import { AnnotationTypes, ResourceItemTypes } from '@praktio/common/lib/constants';
import { isNil } from 'lodash';
import { isLayeredAnnotation } from './slide';
import { getLastNumber, isMissing } from './functions';

const MOVE_ANNOTATION_CLASSES = ['pa-movement-from', 'pa-movement'];

/**
 * Create a wrapper span element for a given annotation subType. This will be used to wrap the span annotations that make up the given annotation.
 * @param subType
 * @returns The wrapper span element.
 */
export const createWrapperAnnotationSpan = (subType: string): HTMLSpanElement => {
  const span = document.createElement('span');
  span.classList.add(`annotation-${subType}`);

  return span;
};

/**
 * Add additional class to the wrapper span element based on the group index.
 * It adds classes to the first, middle, and last groups of a span annotation that span across multiple parent elements, but are part of the same annotation.
 * These classes will be used to determine what gets announced to screen readers.
 * The first group announces the start of the annotation, the middle groups do not announce anything, and the last group announces the end of the annotation.
 * @param wrapperSpan - The wrapper span element to add the class to.
 * @param groups - The groups of span annotations that are part of the same annotation.
 * @param index - The index of the group.
 */
export const addWrapperAnnotationSpanGroupClass = (
  wrapperSpan: HTMLSpanElement,
  groups: Element[][],
  index: number,
): void => {
  // If only one group, return the wrapper span
  // This is because we want the start and end of the annotation to be announced
  if (groups.length === 1) {
    return;
  }

  // Add the following class to only announce the start of the annotation
  if (index === 0) {
    wrapperSpan.classList.add('first-group');
    return;
  }

  // Add the following class to only announce the end of the annotation
  if (index === groups.length - 1) {
    wrapperSpan.classList.add('last-group');
    return;
  }

  // Add the following class to not announce anything
  wrapperSpan.classList.add('middle-group');
};

/**
 * Get the span annotations that are within a given range. Also, take into account if the annotation is layered.
 * If the annotation is layered, the span annotations must contain the rootId in their data-id attribute.
 * @param spanAnnotations - The span annotations to filter.
 * @param annotation - The annotation that contains the range.
 * @returns The span annotations that are within the given range.
 */
export const getSpanAnnotationsInRange = (
  spanAnnotations: Element[],
  annotation: Annotation,
): Element[] => {
  const { startIndex, endIndex, rootId } = annotation;

  // If the annotation doesn't have an index range, return an empty array
  if (isMissing(startIndex) || isMissing(endIndex)) {
    return [];
  }

  return spanAnnotations.filter((span: Element) => {
    const dataId = span.getAttribute('data-id') as string; // Example: 'abc-123'
    const spanIndex = getLastNumber(dataId); // Get the data-id number. Example: 'abc-123' -> 123

    if (isNil(spanIndex)) {
      return false;
    }

    const spanIsInRange = spanIndex >= startIndex && spanIndex <= endIndex;

    // If annotation is layered (aka not a base annotation), check if the span is part of the annotation
    if (isLayeredAnnotation(annotation)) {
      if (isMissing(rootId)) {
        return false;
      }

      return dataId.includes(rootId) && spanIsInRange; // Check if the data-id includes the rootId and is in range
    }

    return spanIsInRange;
  });
};

/**
 * Group span annotations by parent element. This is useful for grouping span annotations that are part of the same annotation, but are in different parent elements.
 * For example, if a deletion annotation spans across table cells in a row, the span annotations will be in different parent elements.
 * @param spanAnnotations - The span annotations to group.
 * @returns An array of arrays of span annotations grouped by parent element.
 */
export const groupSpanAnnotationsByParent = (spanAnnotations: Element[]): Element[][] => {
  const groups: Element[][] = [];
  let currentGroup: Element[] = [];

  spanAnnotations.forEach((span: Element, index: number) => {
    if (index === 0) {
      currentGroup.push(span);
      return;
    }

    const previousSpan = spanAnnotations[index - 1];
    const previousParent = previousSpan.parentElement;
    const currentParent = span.parentElement;

    if (currentParent === previousParent) {
      currentGroup.push(span);
      return;
    }

    groups.push(currentGroup);
    currentGroup = [span];
  });

  groups.push(currentGroup);

  return groups;
};

/**
 * Get the text content of a given span annotation.
 * @param spanAnnotation - The span annotation to get the text content of.
 * @returns The text content of the span annotation.
 */
export const getTextContentFromSpanAnnotations = (spanAnnotations: Element[]): string => {
  return spanAnnotations.map((span: Element) => span.textContent).join('');
};

/**
 * Check if a given span annotation is a move annotation.
 * @param spanAnnotation - The span annotation to check.
 * @returns True if the span annotation is a move annotation, false otherwise.
 */
export const isMoveAnnotation = (annotation: SpanAnnotation) => {
  const { subType, styleClass } = annotation;

  return (
    subType === AnnotationTypes.Dynamic &&
    !!styleClass &&
    MOVE_ANNOTATION_CLASSES.includes(styleClass)
  );
};

/**
 * Get the subType class of a given annotation. This will be used to determine the class to add to the wrapper span element of the annotation.
 * @param annotation - The annotation to get the subType class of.
 * @returns The subType class of the annotation.
 */
export const getSubTypeClass = (annotation: SpanAnnotation) => {
  const { subType, styleClass } = annotation;
  const isMoveAnno = isMoveAnnotation(annotation);
  const moveAnnotationClass = styleClass?.replace('pa-', ''); // Remove 'pa-' from the style class

  return isMoveAnno ? moveAnnotationClass : subType;
};

/**
 * Determines if a span annotation is a markup annotation (with show markup toggled on). Annotation types: addition, deletion, move.
 * @param annotation - The span annotation to check.
 * @returns True if the annotation is a markup annotation, false otherwise.
 */
export const spanAnnotationIsMarkup = (annotation: SpanAnnotation): boolean => {
  const markupAnnotations = [AnnotationTypes.Addition_v2, AnnotationTypes.Delete_v2];
  const { type, subType, showStyling } = annotation;
  const isMoveAnno = isMoveAnnotation(annotation);

  return (
    (type === ResourceItemTypes.Annotation &&
      markupAnnotations.includes(subType as string) &&
      !!showStyling) ||
    isMoveAnno
  );
};

/**
 * Wrap elements that contain certain classes in a span element with a class that corresponds to the annotation subType.
 * This span is used to announce the annotation to screen readers.
 * @param html - The document HTML.
 * @returns The document HTML with the elements wrapped in the span elements.
 */
export const wrapElementsInAnnotationSpan = (html: string | undefined) => {
  if (!html) {
    return '';
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  const additionClasses = ['.pa-addition-styling', `.${AnnotationTypes.Addition_v2}`];
  const deletionClasses = [
    '.pa-red-deletion',
    '.Deletion_v2',
    '.Delete',
    `.${AnnotationTypes.Delete_v2}`,
  ];

  const elements = doc.querySelectorAll([...additionClasses, ...deletionClasses].join(', '));

  elements.forEach((element) => {
    // Skip if element has data-id attribute. These are individual characters that make up an annotation. We don't want to wrap these.
    if (element.hasAttribute('data-id')) {
      return;
    }

    const subType = additionClasses.some((cls) => element.classList.contains(cls.slice(1))) // Slice removes the period from the class name
      ? AnnotationTypes.Addition_v2
      : deletionClasses.some((cls) => element.classList.contains(cls.slice(1)))
      ? AnnotationTypes.Delete_v2
      : null;

    if (!subType) {
      return;
    }

    element.classList.add(`annotation-${subType}`);
  });

  return doc.body.innerHTML;
};

/**
 * Merge annotations with overlapping ranges and the same subType.
 *
 * We wrap annotations in a parent <span> with pseudo-selectors announcing the start and end of annotations.
 * Without merging, overlapping or nested annotations result in multiple wrappers for the same text,
 * causing accessibility issues. Merging ensures that overlapping ranges with the same subType are combined,
 * preventing redundant wrappers.
 */
export const mergeSpanAnnotations = (annotations: SpanAnnotation[]): SpanAnnotation[] => {
  if (!annotations || annotations.length === 0) {
    return [];
  }

  const targetAnnotations = [AnnotationTypes.Addition_v2, AnnotationTypes.Delete_v2];
  const merged = [annotations[0]]; // Start with the first annotation

  annotations.forEach((annotation) => {
    const { subType } = annotation;
    const previous = merged[merged.length - 1];
    const isMoveAnno = isMoveAnnotation(annotation);
    const isTargetAnnotationType = targetAnnotations.includes(subType as string) || isMoveAnno; // Check if the annotation is a target type (addition, deletion or move)
    const isSameSubType = annotation.subType === previous.subType;
    const hasOverlap = annotation.startIndex <= previous.endIndex;
    const hasSameRoot = annotation.rootId === previous.rootId;
    const hasSameStyling = annotation.showStyling === previous.showStyling;

    if (isTargetAnnotationType && isSameSubType && hasOverlap && hasSameRoot && hasSameStyling) {
      // Update the endIndex of the previous annotation to include the current annotation's endIndex
      previous.endIndex = Math.max(previous.endIndex, annotation.endIndex);
      return;
    }

    merged.push(annotation);
  });

  return merged || [];
};

/**
 * Filter annotations to remove annotations with the same index range.
 * We also prioritize delete annotations over other annotations.
 * So, if we had an addition and a delete annotation with the same index range, we would overwrite the addition with the delete.
 * Otherwise, we would keep both annotations.
 * So, if we had an addition and a move annotation with the same index range, we would keep both annotations.
 * @param annotations - The annotations to filter.
 * @returns The filtered annotations.
 */
export const filterAnnotations = (annotations: SpanAnnotation[]): SpanAnnotation[] => {
  const seen = new Map(); // Map to store the index of the first occurrence of an annotation
  const result = [];

  for (const annotation of annotations) {
    const key = `${annotation.startIndex}-${annotation.endIndex}`;

    if (seen.has(key)) {
      // If this is a delete annotation, replace the previous entry
      if (annotation.subType === AnnotationTypes.Delete_v2) {
        result[seen.get(key)] = annotation;
        continue;
      }

      result.push(annotation); // Otherwise, add the annotation

      continue;
    }

    seen.set(key, result.length); // Store the index of the first occurrence of the annotation with a given index range
    result.push(annotation);
  }

  return result;
};
